import { JSX } from "react";
import { useTranslation } from "react-i18next";

import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import { format } from "date-fns/format";
import { parseISO } from "date-fns/parseISO";

import { asTitle } from "utils/Utils";

import {
	BlueprintInfo,
	UserRights
} from "core/blueprint/base/BlueprintService";
import { mergeRights } from "core/blueprint/base/access/Utils";

export type InfoBlueprintDialogProps = {
	info: BlueprintInfo;
	userRights: UserRights[];

	onClose: () => void;
};

export default function InfoBlueprintDialog(
	props: Readonly<InfoBlueprintDialogProps>
): JSX.Element {
	const { t } = useTranslation();

	return (
		<Dialog
			open={true}
			onClick={e => e.stopPropagation()}
			onClose={props.onClose}>
			<DialogTitle>{asTitle(t("infoBlueprint"))}</DialogTitle>
			<DialogContent>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<Typography variant="subtitle1">
							{asTitle(t("general"))}
						</Typography>
					</Grid>
					<Grid container item xs={12} justifyContent="flex-end">
						<Grid item xs={5}>
							<Typography variant="subtitle2">
								{asTitle(t("name"))}
							</Typography>
						</Grid>
						<Grid item xs={6}>
							<Typography>{props.info.name}</Typography>
						</Grid>
					</Grid>
					<Grid container item xs={12} justifyContent="flex-end">
						<Grid item xs={5}>
							<Typography variant="subtitle2">
								{asTitle(t("description"))}
							</Typography>
						</Grid>
						<Grid item xs={6}>
							<Typography>{props.info.description}</Typography>
						</Grid>
					</Grid>

					<Grid item xs={12}>
						<Typography variant="subtitle1">
							{asTitle(t("access"))}
						</Typography>
					</Grid>
					{props.userRights.map(userRights => {
						return (
							<Grid
								key={userRights.user}
								container
								item
								xs={12}
								justifyContent="flex-end">
								<Grid item xs={5}>
									<Typography variant="subtitle2">
										{userRights.user}
									</Typography>
								</Grid>
								<Grid item xs={6}>
									<Typography>
										{t(
											mergeRights(
												userRights.rights
											)?.toLowerCase() ?? ""
										)}
									</Typography>
								</Grid>
							</Grid>
						);
					})}

					<Grid item xs={12}>
						<Typography variant="subtitle1">
							{asTitle(t("history"))}
						</Typography>
					</Grid>
					<Grid container item xs={12} justifyContent="flex-end">
						<Grid item xs={5}>
							<Typography variant="subtitle2">
								{asTitle(t("created"))}
							</Typography>
						</Grid>
						<Grid item xs={6}>
							<Typography>
								{format(
									parseISO(props.info.created),
									"dd/MM/yyyy HH:mm:ss"
								)}
							</Typography>
						</Grid>
					</Grid>
					<Grid container item xs={12} justifyContent="flex-end">
						<Grid item xs={5}>
							<Typography variant="subtitle2">
								{asTitle(t("modified"))}
							</Typography>
						</Grid>
						<Grid item xs={6}>
							<Typography>
								{format(
									parseISO(props.info.modified),
									"dd/MM/yyyy HH:mm:ss"
								)}
							</Typography>
						</Grid>
					</Grid>
					<Grid container item xs={12} justifyContent="flex-end">
						<Grid item xs={5}>
							<Typography variant="subtitle2">
								{asTitle(t("version"))}
							</Typography>
						</Grid>
						<Grid item xs={6}>
							<Typography>{props.info.version}</Typography>
						</Grid>
					</Grid>
				</Grid>
				<DialogActions>
					<Button variant="contained" onClick={props.onClose}>
						{asTitle(t("close"))}
					</Button>
				</DialogActions>
			</DialogContent>
		</Dialog>
	);
}
